/** Text overlay placed on a video by the user */
export interface TextOverlay {
  id: string;
  text: string;
  /** Horizontal position as % of video width (0–100) */
  x: number;
  /** Vertical position as % of video height (0–100) */
  y: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor?: string;
  opacity: number;
  /** Rotation in degrees */
  rotation: number;
  bold: boolean;
  italic: boolean;
  textAlign: "left" | "center" | "right";
  /**
   * Time in seconds when the overlay should appear. Undefined = always visible
   * from t=0. Carries through the publish chain into the v1 OverlayItem wire
   * format so VideoOverlayRenderer's timing gate (`currentTime >= startTime
   * && currentTime <= endTime`) actually fires. Set by the timeline editor's
   * TimelineTextOverlay; preserved verbatim by the legacy compose flow.
   */
  startTime?: number;
  /** Time in seconds when the overlay should disappear. See startTime. */
  endTime?: number;
  /**
   * Which point of the overlay's bounding box sits at (x, y). Historically
   * this was implicit and DIFFERED by platform: the web editors render
   * center-anchored (translate(-50%,-50%)), the Expo renderers top-left.
   * New overlays write their platform's anchor explicitly so the other
   * platform can render them in the right place. Legacy overlays without
   * the field keep each renderer's historical default (web → "center",
   * Expo → "top-left") since their origin platform is unknowable.
   */
  positionAnchor?: "top-left" | "center";
}

export interface OverlayState {
  textOverlays: TextOverlay[];
}

export const DEFAULT_OVERLAY_STATE: OverlayState = {
  textOverlays: [],
};

export const OVERLAY_FONTS = [
  "Default",
  "Plus Jakarta",
  "Bebas",
  "Anton",
  "Oswald",
  "Playfair",
  "Pacifico",
  "Permanent Marker",
  "Bangers",
  "Mono",
] as const;

export const OVERLAY_COLORS = [
  "#FFFFFF",
  "#000000",
  "#FF0000",
  "#FF6B5B",
  "#FF9500",
  "#FFCC00",
  "#34C759",
  "#007AFF",
  "#5856D6",
  "#AF52DE",
  "#FF2D55",
  "#8E8E93",
] as const;

export type OverlayAnchor = "top-left" | "center";

/**
 * Resolve which point of an overlay's box sits at (x, y). Overlays created
 * since positionAnchor shipped carry it explicitly; legacy overlays fall
 * back to the RENDERER's historical default — web renderers always centered
 * (pass "center"), Expo renderers always used the top-left corner (pass
 * "top-left"), and GIF stickers were center-anchored on both (pass
 * "center"). Every renderer and export/burn-in path MUST resolve through
 * this helper so the per-platform legacy matrix lives in one place.
 */
export function resolveOverlayAnchor(
  overlay: { positionAnchor?: OverlayAnchor },
  legacyDefault: OverlayAnchor,
): OverlayAnchor {
  return overlay.positionAnchor ?? legacyDefault;
}

/**
 * The anchor is a required argument: each platform's renderers interpret
 * (x, y) differently for legacy data, so an anchorless overlay silently
 * renders in different places on web vs Expo. Forcing the caller to state
 * its platform's anchor makes that omission a type error.
 */
export function createTextOverlay(
  positionAnchor: OverlayAnchor,
  partial?: Partial<TextOverlay>,
): TextOverlay {
  return {
    id: Math.random().toString(36).slice(2, 10),
    text: "Tap to edit",
    x: 50,
    y: 50,
    fontSize: 32,
    fontFamily: "Default",
    color: "#FFFFFF",
    opacity: 1,
    rotation: 0,
    bold: false,
    italic: false,
    textAlign: "center",
    positionAnchor,
    ...partial,
  };
}

// ========================================
// Versioned overlay wire/storage format (Phase 6A)
// ========================================
//
// Production rows in posts.overlay_metadata currently store the bare
// OverlayState shape: { textOverlays: TextOverlay[] }. The v1 wrapper below
// is the new wire format and will eventually carry mention/hashtag/GIF/poll
// overlays alongside text. Legacy rows are converted on read by
// normalizeOverlayMetadata(), so historical data does not need migrating.

/** Discriminator for an overlay's payload. New types are added in Phase 6B+. */
export type OverlayKind =
  | "text"
  | "caption"
  | "image"
  | "gif"
  | "mention"
  | "hashtag"
  | "link"
  | "poll"
  | "location";

/** Common positioning + lifecycle fields shared by every overlay item. */
export interface OverlayBase {
  id: string;
  type: OverlayKind;
  /** Time in seconds when the overlay should appear. Undefined = always visible. */
  startTime?: number;
  /** Time in seconds when the overlay should disappear. Undefined = always visible. */
  endTime?: number;
  /** Horizontal position as % of video width (0-100). */
  x: number;
  /** Vertical position as % of video height (0-100). */
  y: number;
  /**
   * Which point of the overlay's bounding box is anchored at (x, y).
   * - "top-left" (default): legacy behaviour. Used by text/mention/hashtag.
   * - "center": x/y is the center of the rendered overlay. Used by gif
   *   stickers so drag UX feels like grabbing the middle of the sticker.
   * Renderers and any export/burn-in path MUST honor this.
   */
  positionAnchor?: "top-left" | "center";
  scale?: number;
  rotation?: number;
  opacity?: number;
}

/** Type-specific data for a text overlay (carries the legacy TextOverlay fields). */
export interface TextOverlayPayload {
  text: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor?: string;
  bold: boolean;
  italic: boolean;
  textAlign: "left" | "center" | "right";
}

/** Tappable @-mention pinned to a video. Phase 6B. */
export interface MentionOverlayPayload {
  /** Display handle without the leading '@'. Example: "alice". */
  username: string;
  /** Stable Clerk-style user id captured at compose time, so renames don't break the link. */
  userId?: string;
}

/** Tappable #-hashtag pinned to a video. Phase 6B. */
export interface HashtagOverlayPayload {
  /** Hashtag name without the leading '#'. Example: "music". */
  name: string;
}

/** Animated GIF / static image sticker pinned to a video. Phase 6C. */
export interface GifOverlayPayload {
  /** Direct media URL (GIF or static image). */
  sourceUrl: string;
  /** Source width in pixels, if known. Used to set initial render aspect ratio. */
  width?: number;
  /** Source height in pixels, if known. */
  height?: number;
  /** Provider name for attribution display ("GIPHY", "Tenor", etc.). */
  attribution?: string;
  /** Provider-specific id, kept so we can deep-link back to source later. */
  providerId?: string;
}

/** Discriminated union of overlay items. Add cases as Phase 6B+ ships. */
export type OverlayItem =
  | (OverlayBase & { type: "text"; payload: TextOverlayPayload })
  | (OverlayBase & { type: "mention"; payload: MentionOverlayPayload })
  | (OverlayBase & { type: "hashtag"; payload: HashtagOverlayPayload })
  | (OverlayBase & { type: "gif"; payload: GifOverlayPayload })
  | (OverlayBase & { type: Exclude<OverlayKind, "text" | "mention" | "hashtag" | "gif">; payload: unknown });

export interface OverlayMetadataV1 {
  schemaVersion: 1;
  overlays: OverlayItem[];
}

function randomOverlayId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/** Build a fresh @mention overlay item, positioned center by default. */
export function createMentionOverlay(
  username: string,
  opts: { userId?: string; partial?: Partial<OverlayBase> } = {},
): OverlayItem {
  return {
    id: opts.partial?.id ?? randomOverlayId(),
    type: "mention",
    x: opts.partial?.x ?? 50,
    y: opts.partial?.y ?? 50,
    rotation: opts.partial?.rotation,
    opacity: opts.partial?.opacity,
    scale: opts.partial?.scale,
    startTime: opts.partial?.startTime,
    endTime: opts.partial?.endTime,
    payload: { username, userId: opts.userId },
  };
}

/** Build a fresh #hashtag overlay item, positioned center by default. */
export function createHashtagOverlay(
  name: string,
  partial?: Partial<OverlayBase>,
): OverlayItem {
  return {
    id: partial?.id ?? randomOverlayId(),
    type: "hashtag",
    x: partial?.x ?? 50,
    y: partial?.y ?? 50,
    rotation: partial?.rotation,
    opacity: partial?.opacity,
    scale: partial?.scale,
    startTime: partial?.startTime,
    endTime: partial?.endTime,
    payload: { name },
  };
}

/** Build a fresh GIF / image overlay item, positioned center by default. */
export function createGifOverlay(
  sourceUrl: string,
  opts: {
    width?: number;
    height?: number;
    attribution?: string;
    providerId?: string;
    partial?: Partial<OverlayBase>;
  } = {},
): OverlayItem {
  return {
    id: opts.partial?.id ?? randomOverlayId(),
    type: "gif",
    x: opts.partial?.x ?? 50,
    y: opts.partial?.y ?? 50,
    rotation: opts.partial?.rotation,
    opacity: opts.partial?.opacity,
    // Default scale 0.4 — GIFs render at 40% of container width.
    scale: opts.partial?.scale ?? 0.4,
    // Center anchoring matches the sticker UX (drag from middle).
    positionAnchor: opts.partial?.positionAnchor ?? "center",
    startTime: opts.partial?.startTime,
    endTime: opts.partial?.endTime,
    payload: {
      sourceUrl,
      width: opts.width,
      height: opts.height,
      attribution: opts.attribution,
      providerId: opts.providerId,
    },
  };
}

/**
 * Extract @-usernames referenced by mention overlays in a v1 payload.
 * Mirrors the regex extraction used for content-text mentions so the same
 * notification path can fire when mentions are pinned to a video.
 */
export function mentionUsernamesFromV1(v1: OverlayMetadataV1 | null): string[] {
  if (!v1) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const o of v1.overlays) {
    if (o.type !== "mention") continue;
    const payload = o.payload as MentionOverlayPayload | null;
    if (!payload || typeof payload.username !== "string") continue;
    const u = payload.username.trim().toLowerCase();
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
}

/** Convert a legacy TextOverlay into an OverlayItem of type "text". */
function textOverlayToItem(t: TextOverlay): OverlayItem {
  return {
    id: t.id,
    type: "text",
    x: t.x,
    y: t.y,
    positionAnchor: t.positionAnchor,
    rotation: t.rotation,
    opacity: t.opacity,
    startTime: t.startTime,
    endTime: t.endTime,
    payload: {
      text: t.text,
      fontSize: t.fontSize,
      fontFamily: t.fontFamily,
      color: t.color,
      backgroundColor: t.backgroundColor,
      bold: t.bold,
      italic: t.italic,
      textAlign: t.textAlign,
    },
  };
}

/**
 * Normalize raw `overlay_metadata` JSONB into the v1 shape, regardless of
 * whether the row is legacy (bare OverlayState) or already v1. Returns null
 * for null/undefined/malformed input. Renderers should always read through
 * this — never iterate the raw JSONB directly.
 */
export function normalizeOverlayMetadata(raw: unknown): OverlayMetadataV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  // Already v1 — pass through after a basic shape check.
  if (obj.schemaVersion === 1 && Array.isArray(obj.overlays)) {
    return { schemaVersion: 1, overlays: obj.overlays as OverlayItem[] };
  }

  // Legacy OverlayState — wrap each TextOverlay as an OverlayItem.
  if (Array.isArray(obj.textOverlays)) {
    const items = (obj.textOverlays as TextOverlay[])
      .filter(t => t && typeof t === "object" && typeof t.id === "string")
      .map(textOverlayToItem);
    return { schemaVersion: 1, overlays: items };
  }

  return null;
}

/** Build the v1 wire format from an in-memory OverlayState (text-only today). */
export function overlayStateToV1(state: OverlayState | null | undefined): OverlayMetadataV1 | null {
  if (!state || !Array.isArray(state.textOverlays) || state.textOverlays.length === 0) return null;
  return {
    schemaVersion: 1,
    overlays: state.textOverlays.map(textOverlayToItem),
  };
}

/** Convenience: pull just the text overlays out of a normalized v1 payload. */
export function textOverlaysFromV1(v1: OverlayMetadataV1 | null): TextOverlay[] {
  if (!v1) return [];
  return v1.overlays
    .filter((o): o is OverlayBase & { type: "text"; payload: TextOverlayPayload } =>
      o.type === "text" && o.payload != null && typeof o.payload === "object",
    )
    .map(o => ({
      id: o.id,
      text: o.payload.text,
      x: o.x,
      y: o.y,
      positionAnchor: o.positionAnchor,
      fontSize: o.payload.fontSize,
      fontFamily: o.payload.fontFamily,
      color: o.payload.color,
      backgroundColor: o.payload.backgroundColor,
      opacity: o.opacity ?? 1,
      rotation: o.rotation ?? 0,
      bold: o.payload.bold,
      italic: o.payload.italic,
      textAlign: o.payload.textAlign,
    }));
}
